import React, { useId } from 'react';
import moment, { Moment } from 'moment';
import { Box, Chip, TextField, Typography } from '@material-ui/core';
import { DateTimePicker } from '@material-ui/pickers';
import { useTranslation } from 'react-i18next';
import { UploadCloud } from 'react-feather';
import { Dropzone } from 'src/components/Dropzone';
import { TActionHandler } from 'src/modules/assistant/actions/types';
import { TEditableDraft } from './types';
import { useDraftStyles } from './styles';

const MULTILINE_ROWS = 3;
const DATE_MINUTES_STEP = 10;
const UPLOAD_ICON_SIZE = 21;

interface DraftFieldsProps {
    draft: TEditableDraft;
    handler: TActionHandler | null;
    onChange: (changes: Partial<TEditableDraft>) => void;
}

/**
 * Editable fields of a draft: text fields declared by the action handler, the date
 * for schedulable actions and the documents when the customer asked for them.
 */
export const DraftFields = ({ draft, handler, onChange }: DraftFieldsProps) => {
    const classes = useDraftStyles();
    const { t } = useTranslation();
    const fieldId = useId();
    const attachments = draft.attachments || [];

    return (
        <>
            {(handler?.fields || []).map(([name, label, multiline]) => (
                <TextField
                    fullWidth
                    id={`${fieldId}-${name}`}
                    key={name}
                    label={t(label)}
                    multiline={multiline}
                    onChange={(event) =>
                        onChange({ [name]: event.target.value })
                    }
                    rows={multiline ? MULTILINE_ROWS : undefined}
                    size="small"
                    value={(draft[name] as string) || ''}
                    variant="outlined"
                />
            ))}
            {handler?.schedulable && (
                <DateTimePicker
                    disablePast
                    fullWidth
                    id={`${fieldId}-scheduledAt`}
                    inputVariant="outlined"
                    label={t('Assistant.ActionFieldDate')}
                    minutesStep={DATE_MINUTES_STEP}
                    onChange={(date: Moment | null) =>
                        onChange({ scheduledAt: date ? date.format() : null })
                    }
                    size="small"
                    value={draft.scheduledAt ? moment(draft.scheduledAt) : null}
                />
            )}
            {handler?.acceptsDocuments && draft.documentationRequested && (
                <>
                    <Dropzone
                        handleAdd={(files: File[]) =>
                            onChange({
                                attachments: [...attachments, ...files],
                            })
                        }
                        multiple
                    >
                        <div className={classes.fileArea}>
                            <UploadCloud
                                aria-hidden="true"
                                size={UPLOAD_ICON_SIZE}
                            />
                            <span>
                                <Typography variant="subtitle2">
                                    {t('Assistant.ActionFieldDocuments')}
                                </Typography>
                                <Typography
                                    color="textSecondary"
                                    variant="caption"
                                >
                                    {t('Assistant.ActionFieldDocumentsHint')}
                                </Typography>
                            </span>
                        </div>
                    </Dropzone>
                    {!!attachments.length && (
                        <Box display="flex" flexWrap="wrap" gridGap={5}>
                            {attachments.map((file, index) => (
                                <Chip
                                    color="primary"
                                    key={`${file.name}-${index}`}
                                    label={file.name}
                                    onDelete={() =>
                                        onChange({
                                            attachments: attachments.filter(
                                                (_, itemIndex) =>
                                                    itemIndex !== index
                                            ),
                                        })
                                    }
                                    size="small"
                                    variant="outlined"
                                />
                            ))}
                        </Box>
                    )}
                </>
            )}
        </>
    );
};
